/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { FieldArrayRenderProps } from 'react-final-form-arrays'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router-dom'
import { Avatar, IconButton } from '@mui/material'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import Table, { TableProps } from 'components/Table'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { searchByStoryteller_searchByStoryteller_items as Storyteller } from '__generatedTypes__/searchByStoryteller'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import * as teamMembersClasses from 'pagesNew/TeamMembers/TeamMembersPlatformList/styles'
import { SuccessStoriesSearch } from '../SuccessStoriesSearch'
import { FormValues } from '../types'
import * as classes from './styles'

type Props = {
  platform: Platform
  onChange: (name: keyof FormValues, value?: Storyteller[]) => void
  fields: FieldArrayRenderProps<Storyteller, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Storyteller, HTMLElement>['meta']
  linkParams?: URLSearchParams
}

export const SuccessStoriesFieldArray: FC<Props> = ({ platform, fields, meta, onChange, linkParams }) => {
  const { getLink } = useLink()
  const fieldArray = fields.value ?? []

  const handleSort = (newFieldArray: Storyteller[]) => {
    onChange('articles', newFieldArray)
  }

  const handleAdd = (successStory: Storyteller) => {
    fields.push(successStory)
  }

  const SuccessStoriesColumns: TableProps<Storyteller>['columns'] = [
    {
      renderCell: (_successStory, index) => (
        <Typography variant="body2" css={classes.rowIndex}>
          {index + 1}
        </Typography>
      ),
      flex: '0 1 30px',
    },
    {
      renderCell: (successStory) => (
        <div css={classes.avatarWrapper}>
          <Avatar src={successStory.getCoverImage ?? undefined} alt="Avatar" />
        </div>
      ),
      flex: '0 1 30px',
    },
    {
      renderCell: (successStory) => (
        <Typography variant="body2" css={[teamMembersClasses.longText, classes.longText]}>
          {successStory.fullName}
        </Typography>
      ),
    },
    {
      renderCell: (successStory) => (
        <Link to={getLink(cmsRoutes.EDIT_SUCCESS_STORY, { successStoryId: successStory.id }, linkParams)}>
          <IconButton css={teamMembersClasses.editIcon}>
            <EditIcon />
          </IconButton>
        </Link>
      ),
      flex: '0 1 29px',
    },
    {
      renderCell: (_successStory, index: number) => (
        <IconButton onClick={() => fields.remove(index)} css={teamMembersClasses.editIcon}>
          <CrossIcon />
        </IconButton>
      ),
      flex: '0 1 29px',
    },
  ]

  return (
    <div css={classes.root}>
      <Typography variant="body2" css={classes.label}>
        Order of Success Stories
      </Typography>

      <div css={classes.search}>
        <SuccessStoriesSearch
          platform={platform}
          value={fieldArray}
          onAdd={handleAdd}
          error={meta.touched && meta.error}
          formHelperText={meta.touched && typeof meta.error === 'string' ? meta.error : undefined}
        />
      </div>

      {fieldArray.length ? (
        <Table
          css={[teamMembersClasses.table, classes.table]}
          columns={SuccessStoriesColumns}
          items={fieldArray}
          hideHeader
          isSortable
          onSort={handleSort}
        />
      ) : (
        <Typography variant="body2" css={classes.emptyLabel}>
          No Success Stories added
        </Typography>
      )}
    </div>
  )
}
